import React from "react";
import { Helmet } from "react-helmet-async";

const Services = () => {
  return (
    <>
      <Helmet>
        <title>Software Development Services | UI/UX, Web, Mobile, AI, Cloud</title>
        <meta
          name="description"
          content="Full-stack software development services: UI/UX design, web development, mobile apps, AI & system design, and cloud solutions. Results-driven delivery and scalable engineering."
        />
        <link rel="canonical" href="https://www.uniwaystart.com/services" />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Software Development Services | UI/UX, Web, Mobile, AI, Cloud" />
        <meta
          property="og:description"
          content="Full-stack software development services: UI/UX design, web development, mobile apps, AI & system design, and cloud solutions."
        />
        <meta property="og:url" content="https://www.uniwaystart.com/services" />
        <meta property="og:image" content="https://www.uniwaystart.com/images/Uniway_Logo.png" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Software Development Services | UI/UX, Web, Mobile, AI, Cloud" />
        <meta
          name="twitter:description"
          content="Full-stack software development services: UI/UX design, web development, mobile apps, AI & system design, and cloud solutions."
        />
        <meta name="twitter:image" content="https://www.uniwaystart.com/images/Uniway_Logo.png" />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            name: "Software Development Services",
            url: "https://www.uniwaystart.com/services",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "UI/UX Design",
                  serviceType: "Product and interface design",
                  areaServed: "Worldwide"
                }
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Mobile App Design",
                  serviceType: "iOS and Android design",
                  areaServed: "Worldwide"
                }
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "AI and System Design",
                  serviceType: "Machine learning and systems architecture",
                  areaServed: "Worldwide"
                }
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Mobile and Desktop App Development",
                  serviceType: "Cross-platform application development",
                  areaServed: "Worldwide"
                }
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Web Development",
                  serviceType: "Full-stack web engineering",
                  areaServed: "Worldwide"
                }
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Cloud Solutions and Development",
                  serviceType: "Cloud architecture and DevOps",
                  areaServed: "Worldwide"
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <section id="services" aria-labelledby="services-heading" className="lg:w-[90%] w-full mx-auto lg:mt-20 mt-32">
        <div className="text-center mb-16">
          <h1 id="services-heading" className="text-4xl font-bold text-gray-900 mb-4">Our Services</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Comprehensive software development solutions tailored to your business needs. 
          From AI-powered applications to cloud infrastructure, we deliver excellence in every project.
        </p>
      </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <article className="group relative w-full rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-blue-100" itemScope itemType="https://schema.org/Service">
          <meta itemProp="serviceType" content="UI/UX Design" />
          <div className="relative h-[200px] overflow-hidden rounded-t-xl" aria-label="UI/UX Design" itemProp="image">
            <img
              src="/images/slider-10.png"
              alt="UI/UX design service showcasing wireframes and high-fidelity prototypes"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              width="640"
              height="360"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute top-3 left-3 bg-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full">Design</span>
          </div>
          <div className="card-body p-6">
            <h2 className="text-xl text-blue-700 text-center font-semibold mb-3">UI/UX</h2>
            <p className="card-text text-sm text-center text-gray-700 mb-4">
              We deliver exceptional UI/UX design by blending intuitive
              functionality with striking visuals, creating user-friendly
              interfaces that captivate and convert.
            </p>
            <div className="text-xs text-gray-500 text-center">
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">Figma</span>
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">Adobe XD</span>
              <span className="bg-gray-100 px-2 py-1 rounded">Sketch</span>
            </div>
            <div className="mt-5 flex justify-center">
              <a href="#contact" className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400">
                Learn more
              </a>
            </div>
          </div>
        </article>
        <article className="group relative w-full rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-blue-100" itemScope itemType="https://schema.org/Service">
          <meta itemProp="serviceType" content="Mobile App Design" />
          <div className="relative h-[200px] overflow-hidden rounded-t-xl" aria-label="Mobile App Design" itemProp="image">
            <img
              src="/images/Appdevelop.png"
              alt="Mobile app design service with iOS and Android interface screens"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              width="640"
              height="360"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute top-3 left-3 bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full">Mobile</span>
          </div>
          <div className="card-body p-6">
            <h2 className="text-xl text-blue-700 text-center font-semibold mb-3">Mobile App Design</h2>
            <p className="card-text text-sm text-center text-gray-700 mb-4">
              We craft intuitive and engaging mobile app designs that drive user adoption and achieve your business goals.
            </p>
            <div className="text-xs text-gray-500 text-center">
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">iOS</span>
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">Android</span>
              <span className="bg-gray-100 px-2 py-1 rounded">React Native</span>
            </div>
            <div className="mt-5 flex justify-center">
              <a href="#contact" className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400">
                Learn more
              </a>
            </div>
          </div>
        </article>
        <article className="group relative w-full rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-blue-100" itemScope itemType="https://schema.org/Service">
          <meta itemProp="serviceType" content="AI and System Design" />
          <div className="relative h-[200px] overflow-hidden rounded-t-xl" aria-label="AI and System Design" itemProp="image">
            <img
              src="/images/Advisory.png"
              alt="AI and system design service illustrating data pipelines and ML models"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              width="640"
              height="360"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute top-3 left-3 bg-purple-600 text-white text-xs font-medium px-2 py-1 rounded-full">AI</span>
          </div>
          <div className="card-body p-6">
            <h2 className="text-xl text-blue-700 text-center font-semibold mb-3">AI and System Design</h2>
            <p className="card-text text-sm text-center text-gray-700 mb-4">
              We specialize in AI and system design, creating intelligent solutions
              that enhance efficiency and drive innovation.
            </p>
            <div className="text-xs text-gray-500 text-center">
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">TensorFlow</span>
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">PyTorch</span>
              <span className="bg-gray-100 px-2 py-1 rounded">OpenAI</span>
            </div>
            <div className="mt-5 flex justify-center">
              <a href="#contact" className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400">
                Learn more
              </a>
            </div>
          </div>
        </article>
        <article className="group relative w-full rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-blue-100" itemScope itemType="https://schema.org/Service">
          <meta itemProp="serviceType" content="Mobile and Desktop App Development" />
          <div className="relative h-[200px] overflow-hidden rounded-t-xl" aria-label="App Development" itemProp="image">
            <img
              src="/images/Appdevelop2.png"
              alt="Cross-platform app development showing mobile and desktop devices"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              width="640"
              height="360"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute top-3 left-3 bg-rose-600 text-white text-xs font-medium px-2 py-1 rounded-full">Apps</span>
          </div>
          <div className="card-body p-6">
            <h2 className="text-xl text-blue-700 text-center font-semibold mb-3">Mobile App and Desktop App</h2>
            <p className="card-text text-sm text-center text-gray-700 mb-4">
              Our app development services deliver robust, scalable applications
              tailored to your needs. We ensure seamless performance and
              intuitive user experiences.
            </p>
            <div className="text-xs text-gray-500 text-center">
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">React Native</span>
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">Flutter</span>
              <span className="bg-gray-100 px-2 py-1 rounded">Electron</span>
            </div>
            <div className="mt-5 flex justify-center">
              <a href="#contact" className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400">
                Learn more
              </a>
            </div>
          </div>
        </article>
        <article className="group relative w-full rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-blue-100" itemScope itemType="https://schema.org/Service">
          <meta itemProp="serviceType" content="Web Development" />
          <div className="relative h-[200px] overflow-hidden rounded-t-xl" aria-label="Web Development" itemProp="image">
            <img
              src="/images/slide-4.png"
              alt="Web development service with responsive website layouts on multiple devices"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              width="640"
              height="360"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute top-3 left-3 bg-amber-600 text-white text-xs font-medium px-2 py-1 rounded-full">Web</span>
          </div>
          <div className="card-body p-6">
            <h2 className="text-xl text-blue-700 text-center font-semibold mb-3">Web Development</h2>
            <p className="card-text text-sm text-center text-gray-700 mb-4">
              Our web development services build dynamic, high-performance
              websites with cutting-edge technology. We focus on creating
              scalable, secure, and responsive solutions.
            </p>
            <div className="text-xs text-gray-500 text-center">
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">React</span>
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">Node.js</span>
              <span className="bg-gray-100 px-2 py-1 rounded">Next.js</span>
            </div>
            <div className="mt-5 flex justify-center">
              <a href="#contact" className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400">
                Learn more
              </a>
            </div>
          </div>
        </article>
        <article className="group relative w-full rounded-xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-1 hover:ring-blue-100" itemScope itemType="https://schema.org/Service">
          <meta itemProp="serviceType" content="Cloud Solutions and Development" />
          <div className="relative h-[200px] overflow-hidden rounded-t-xl" aria-label="Cloud Solutions and Development" itemProp="image">
            <img
              src="/images/Microservice.png"
              alt="Cloud solutions service diagram with microservices and Kubernetes"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              width="640"
              height="360"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute top-3 left-3 bg-teal-600 text-white text-xs font-medium px-2 py-1 rounded-full">Cloud</span>
          </div>
          <div className="card-body p-6">
            <h2 className="text-xl text-blue-700 text-center font-semibold mb-3">
              Cloud Solutions and Development
            </h2>
            <p className="card-text text-sm text-center text-gray-700 mb-4">
              Our cloud solutions and development services offer scalable,
              secure, and efficient cloud-based systems.
            </p>
            <div className="text-xs text-gray-500 text-center">
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">AWS</span>
              <span className="bg-gray-100 px-2 py-1 rounded mr-1">Azure</span>
              <span className="bg-gray-100 px-2 py-1 rounded">GCP</span>
            </div>
            <div className="mt-5 flex justify-center">
              <a href="#contact" className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400">
                Learn more
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
    </>
  );
};

export default Services;